import React, { useEffect, useState, useCallback } from "react";
import { Search, X } from "lucide-react";
import api from "@/lib/api";
import { StatusPill } from "@/components/common";

const FILTERS = ["all", "accepted", "partial", "failed", "uncertain", "sandbox", "legacy"];

export default function Logs() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    const params = { direction: "outbound", limit: 200 };
    if (status !== "all") params.status = status;
    if (search) params.search = search;
    api.get("/emails", { params }).then((r) => setRows(r.data)).catch(() => {});
  }, [status, search]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-6 fade-up">
      <div>
        <h1 className="display text-2xl font-bold">Activity Logs</h1>
        <p className="mt-1 text-sm text-slate-400">Every outbound message with its SMTP response.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input data-testid="logs-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search subject or recipient…"
            className="w-full rounded-lg border border-slate-700 bg-slate-900/60 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-emerald-500/60" />
        </div>
        <div className="flex flex-wrap gap-1 rounded-lg border border-slate-800 bg-slate-900/40 p-1">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`logs-filter-${f}`} onClick={() => setStatus(f)}
              className={`mono rounded-md px-3 py-1.5 text-xs capitalize transition-colors ${status === f ? "bg-emerald-500/15 text-emerald-400" : "text-slate-400 hover:text-white"}`}>{f}</button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900/40">
        <table className="responsive-mail-table w-full text-sm">
          <thead className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium">Recipient</th>
              <th className="px-5 py-3 font-medium">Subject</th>
              <th className="hidden px-5 py-3 font-medium md:table-cell">Source</th>
              <th className="hidden px-5 py-3 font-medium sm:table-cell">Sent</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {rows.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">No messages found.</td></tr>}
            {rows.map((e) => (
              <tr key={e.id} data-testid={`email-log-row-${e.id}`} onClick={() => setSelected(e)} className="cursor-pointer transition-colors hover:bg-slate-800/40">
                <td className="px-5 py-3"><StatusPill status={e.delivery_mode === "legacy" ? "legacy" : e.status} /></td>
                <td className="mono px-5 py-3 text-slate-300">{e.to_addrs?.join(", ")}</td>
                <td className="px-5 py-3">{e.subject}</td>
                <td className="mono hidden px-5 py-3 text-xs text-slate-500 md:table-cell">{e.source}</td>
                <td className="hidden px-5 py-3 text-xs text-slate-500 sm:table-cell">{new Date(e.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={() => setSelected(null)}>
          <div className="h-full w-full max-w-md overflow-y-auto border-l border-slate-800 bg-[#0B0F17] p-6" onClick={(e) => e.stopPropagation()} data-testid="log-detail-drawer">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="display text-lg font-semibold">Message detail</h2>
              <button data-testid="log-detail-close" aria-label="Close detail" onClick={() => setSelected(null)} className="text-slate-500 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4 text-sm">
              <div className="flex items-center gap-2"><StatusPill status={selected.delivery_mode === "legacy" ? "legacy" : selected.status} /></div>
              <p data-testid="log-delivery-mode" className="text-xs text-slate-400">Mode: {selected.delivery_mode || "legacy"}. {selected.delivery_mode === "legacy" ? "Historical log; internet delivery not confirmed." : selected.status === "accepted" ? "Recipient server accepted; inbox placement not confirmed." : ""}</p>
              {[["From", selected.from_addr], ["To", selected.to_addrs?.join(", ")], ["Subject", selected.subject], ["Message-ID", selected.message_id], ["SMTP response", selected.smtp_response], ["Size", `${selected.size} bytes`], ["Sent", new Date(selected.created_at).toLocaleString()]].map(([k, v]) => (
                <div key={k}>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">{k}</div>
                  <div className="mono mt-1 break-all text-slate-300">{v}</div>
                </div>
              ))}
              {(selected.html || selected.text) && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">Body</div>
                  <div className="mono mt-1 whitespace-pre-wrap break-all rounded-lg border border-slate-800 bg-slate-950 p-3 text-xs text-slate-400">{selected.html || selected.text}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
